/**
 * Quiz Preview Component
 * Shows the quiz exactly as students would see it, but without saving responses
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiClock, FiBookOpen, FiCalendar, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;

  &:hover {
    background: var(--background-hover);
    border-color: var(--primary-color);
  }
`;


const QuizInfo = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const QuizTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
`;

const QuizMeta = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const PreviewNotice = styled.div`
  background: linear-gradient(135deg, rgba(246, 224, 94, 0.1) 0%, rgba(214, 158, 46, 0.1) 100%);
  border: 1px solid var(--warning-color);
  border-radius: 0;
  padding: 1rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--warning-dark);
  font-weight: 500;
  font-size: 0.875rem;
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const QuestionCard = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const QuestionNumber = styled.span`
  background: var(--primary-color);
  color: var(--text-inverse);
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 600;
`;

const QuestionType = styled.span`
  background: var(--background-secondary);
  color: var(--text-secondary);
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
`;

const QuestionPrompt = styled.p`
  font-size: 0.95rem;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  line-height: 1.6;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const CorrectOptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(104, 211, 145, 0.1);
  border: 1px solid var(--success-color);
  border-radius: 0;
  color: var(--success-dark);
  font-weight: 500;
  font-size: 0.875rem;
`;

const RadioButton = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
`;


const TextInput = styled.div`
  width: 100%;
  padding: 0.75rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  color: var(--text-muted);
  font-style: italic;
  font-size: 0.875rem;
`;

const MatchingHeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const MatchingItemGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 0.75rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const CustomDropdown = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  
  &:hover {
    border-color: var(--primary-color);
  }
  
  &[data-has-value="true"] {
    background: var(--background-primary);
    color: var(--text-primary);
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.875rem;
    min-height: 44px;
  }
`;

const DropdownIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 0.5rem;
`;

const DropdownMenu = styled.div`
  position: fixed;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  min-width: 200px;
  max-width: 400px;
  
  @media (max-width: 768px) {
    max-width: calc(100vw - 32px);
    max-height: 180px;
  }
`;

const DropdownOption = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: var(--background-primary);
  border: none;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  text-align: left;
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  
  &:hover {
    background: var(--background-hover);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &.selected {
    background: var(--primary-color);
    color: var(--text-inverse);
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.875rem;
    min-height: 44px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  text-align: center;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--error-color);
  text-align: center;
`;

function QuizPreview() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [dropdownPositions, setDropdownPositions] = useState({});
  const dropdownRefs = React.useRef({});

  const fetchQuiz = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await quizApiService.getQuiz(quizId);
      if (response.success) {
        setQuiz(response.data);
      } else {
        setError(response.message || 'Failed to load quiz');
      }
    } catch (err) {
      console.error('Error fetching quiz:', err);
      setError(err.message || 'Failed to load quiz');
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('[data-dropdown]')) {
        return;
      }
      setOpenDropdowns({});
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatQuestionType = (type) => {
    switch (type) {
      case 'multiple_choice': return 'Multiple Choice';
      case 'true_false': return 'True/False';
      case 'fill_blank': return 'Fill in the Blank';
      case 'matching': return 'Matching';
      default: return type;
    }
  };


  const formatFillBlankPrompt = (prompt) => {
    if (!prompt) return '';
    // Replace single underscores with 5 underscores for better visual indication
    return prompt.replace(/_{1,}/g, '_____');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FiBookOpen size={48} />
          <h3>Loading quiz preview...</h3>
          <p>Please wait while we fetch the quiz details.</p>
        </LoadingState>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorState>
          <h3>Error loading quiz</h3>
          <p>{error}</p>
          <BackButton onClick={() => navigate('/instructor/quizzes')}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </BackButton>
        </ErrorState>
      </Container>
    );
  }

  if (!quiz) {
    return (
      <Container>
        <ErrorState>
          <h3>Quiz not found</h3>
          <p>The quiz you're looking for doesn't exist or you don't have permission to view it.</p>
          <BackButton onClick={() => navigate('/instructor/quizzes')}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </BackButton>
        </ErrorState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/instructor/quizzes')}>
          <FiArrowLeft size={16} />
          Back to Quizzes
        </BackButton>
        <div></div>
      </Header>

      <PreviewNotice>
        <FiBookOpen size={20} />
        This is a preview of how students will see this quiz. Correct answers are visible below for instructors only.
      </PreviewNotice>

      <QuizInfo>
        <QuizTitle>{quiz.title}</QuizTitle>
        {quiz.description && (
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            {quiz.description}
          </p>
        )}
        <QuizMeta>
          <MetaItem>
            <FiBookOpen size={16} />
            Course: {quiz.course_name || 'No course assigned'}
          </MetaItem>
          <MetaItem>
            <FiClock size={16} />
            Time Limit: Set when assigned
          </MetaItem>
          <MetaItem>
            <FiCalendar size={16} />
            Due Date: {formatDate(quiz.due_at)}
          </MetaItem>
          <MetaItem>
            Total Questions: {quiz.questions?.length || 0}
          </MetaItem>
        </QuizMeta>
      </QuizInfo>

      <QuestionsList>
        {quiz.questions?.map((question, index) => (
          <QuestionCard key={question.id}>
            <QuestionHeader>
              <QuestionNumber>Question {index + 1}</QuestionNumber>
              <QuestionType>{formatQuestionType(question.type)}</QuestionType>
            </QuestionHeader>
            
            <QuestionPrompt>
              {question.type === 'fill_blank' ? formatFillBlankPrompt(question.prompt) : question.prompt}
            </QuestionPrompt>
            
            {question.type === 'multiple_choice' && (
              <OptionsList>
                {question.options?.map((option, optIndex) => {
                  const OptionComponent = option.is_correct ? CorrectOptionItem : OptionItem;
                  return (
                    <OptionComponent key={option.id}>
                      <RadioButton style={{ 
                        backgroundColor: option.is_correct ? 'var(--success-color)' : 'var(--background-primary)',
                        borderColor: option.is_correct ? 'var(--success-color)' : 'var(--border-color)'
                      }} />
                      <span>{option.option_text}</span>
                    </OptionComponent>
                  );
                })}
              </OptionsList>
            )}
            
            {question.type === 'true_false' && (
              <OptionsList>
                {question.options?.map((option, optIndex) => {
                  const OptionComponent = option.is_correct ? CorrectOptionItem : OptionItem;
                  return (
                    <OptionComponent key={option.id}>
                      <RadioButton style={{ 
                        backgroundColor: option.is_correct ? 'var(--success-color)' : 'var(--background-primary)',
                        borderColor: option.is_correct ? 'var(--success-color)' : 'var(--border-color)'
                      }} />
                      <span>{option.option_text}</span>
                    </OptionComponent>
                  );
                })}
              </OptionsList>
            )}
            
            {question.type === 'fill_blank' && (
              <>
                <TextInput>
                  [Student will type their answer here]
                </TextInput>
                {question.settings?.acceptableAnswers && question.settings.acceptableAnswers.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    {question.settings.acceptableAnswers.map((answer, ansIndex) => {
                      const blankCount = question.prompt ? question.prompt.match(/_{1,}/g)?.length || 0 : 0;
                      const hasBlanks = blankCount > 0;
                      const blankNumber = ansIndex + 1;
                      
                      return (
                        <CorrectOptionItem key={ansIndex} style={{ marginTop: ansIndex > 0 ? '0.5rem' : '0' }}>
                          <span>
                            {hasBlanks ? `Blank ${blankNumber}: ` : 'Answer: '}
                            {answer}
                          </span>
                        </CorrectOptionItem>
                      );
                    })}
                  </div>
                )}
                {/* Fallback for old format */}
                {!question.settings?.acceptableAnswers && question.settings?.correct_answer && (
                  <CorrectOptionItem style={{ marginTop: '0.5rem' }}>
                    <span>Correct Answer: {question.settings.correct_answer}</span>
                  </CorrectOptionItem>
                )}
              </>
            )}

            {question.type === 'matching' && (() => {
              const settings = question.settings || {};
              const leftItems = settings.leftItems || [];
              const rightItems = settings.rightItems || [];
              
              // Shuffle arrays for display (deterministic based on question ID) - same as student view
              const shuffleWithMapping = (array, seed) => {
                // Filter out empty items and create mapping
                const validItems = [];
                const originalToValid = {}; // originalIndex -> validIndex
                array.forEach((item, originalIndex) => {
                  if (item && item.trim()) {
                    originalToValid[originalIndex] = validItems.length;
                    validItems.push({ item, originalIndex });
                  }
                });
                
                if (validItems.length === 0) {
                  return { shuffledArray: [], shuffleMapping: {}, reverseMapping: {} };
                }
                
                // Create array of valid indices to shuffle
                const validIndices = validItems.map((_, i) => i);
                const shuffledValidIndices = [...validIndices];
                
                // Use a simple seeded random number generator
                let seedValue = seed;
                const seededRandom = () => {
                  seedValue = (seedValue * 9301 + 49297) % 233280;
                  return seedValue / 233280;
                };
                
                // Fisher-Yates shuffle with seeded random
                for (let i = shuffledValidIndices.length - 1; i > 0; i--) {
                  const j = Math.floor(seededRandom() * (i + 1));
                  [shuffledValidIndices[i], shuffledValidIndices[j]] = [shuffledValidIndices[j], shuffledValidIndices[i]];
                }
                
                // Create shuffled array and mapping
                const shuffledArray = shuffledValidIndices.map(validIndex => validItems[validIndex].item);
                const shuffleMapping = {}; // shuffledIndex -> originalIndex
                const reverseMapping = {}; // originalIndex -> shuffledIndex
                
                shuffledValidIndices.forEach((validIndex, shuffledIndex) => {
                  const originalIndex = validItems[validIndex].originalIndex;
                  shuffleMapping[shuffledIndex] = originalIndex;
                  reverseMapping[originalIndex] = shuffledIndex;
                });
                
                return { shuffledArray, shuffleMapping, reverseMapping };
              };
              
              // Use question ID as seed for deterministic shuffling (same as student view)
              const seed = question.id || 0;
              const leftShuffle = shuffleWithMapping(leftItems, seed);
              const rightShuffle = shuffleWithMapping(rightItems, seed + 1000); // Different seed for right items
              
              const shuffledLeftItems = leftShuffle.shuffledArray;
              const shuffledRightItems = rightShuffle.shuffledArray;
              
              return (
                <>
                  <div style={{ marginTop: '1rem' }}>
                    <MatchingHeaderGrid>
                      <div>
                        <h4 style={{ 
                          fontSize: '1rem', 
                          fontWeight: '600', 
                          marginBottom: '1rem',
                          color: 'var(--text-primary)'
                        }}>
                          Terms
                        </h4>
                      </div>
                      <div>
                        <h4 style={{ 
                          fontSize: '1rem', 
                          fontWeight: '600', 
                          marginBottom: '1rem',
                          color: 'var(--text-primary)'
                        }}>
                          Definitions
                        </h4>
                      </div>
                    </MatchingHeaderGrid>
                    
                    {shuffledLeftItems.map((leftItem, shuffledLeftIndex) => {
                      if (!leftItem || !leftItem.trim()) return null;
                      
                      return (
                        <MatchingItemGrid key={shuffledLeftIndex}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            fontWeight: '500',
                            color: 'var(--text-primary)',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {leftItem}
                          </div>
                          <CustomDropdown 
                            data-dropdown
                            ref={el => {
                              const dropdownKey = `${question.id}-${shuffledLeftIndex}`;
                              dropdownRefs.current[dropdownKey] = el;
                            }}
                          >
                            <DropdownButton
                              type="button"
                              data-has-value="false"
                              onClick={() => {
                                const dropdownKey = `${question.id}-${shuffledLeftIndex}`;
                                const button = dropdownRefs.current[dropdownKey]?.querySelector('button');
                                if (button) {
                                  const rect = button.getBoundingClientRect();
                                  const isMobile = window.innerWidth <= 768;
                                  setDropdownPositions(prev => ({
                                    ...prev,
                                    [dropdownKey]: {
                                      top: rect.bottom + window.scrollY + 4,
                                      left: isMobile ? Math.max(8, rect.left + window.scrollX - 8) : rect.left + window.scrollX,
                                      width: isMobile ? Math.min(rect.width, window.innerWidth - 16) : rect.width
                                    }
                                  }));
                                }
                                setOpenDropdowns(prev => ({
                                  ...prev,
                                  [dropdownKey]: !prev[dropdownKey]
                                }));
                              }}
                            >
                              Select a match...
                              <DropdownIcon>
                                {openDropdowns[`${question.id}-${shuffledLeftIndex}`] ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                              </DropdownIcon>
                            </DropdownButton>
                            {openDropdowns[`${question.id}-${shuffledLeftIndex}`] && createPortal(
                              <DropdownMenu 
                                data-dropdown
                                position={dropdownPositions[`${question.id}-${shuffledLeftIndex}`]}
                                style={{
                                  top: dropdownPositions[`${question.id}-${shuffledLeftIndex}`]?.top + 'px',
                                  left: dropdownPositions[`${question.id}-${shuffledLeftIndex}`]?.left + 'px',
                                  width: dropdownPositions[`${question.id}-${shuffledLeftIndex}`]?.width + 'px'
                                }}
                              >
                                <DropdownOption
                                  type="button"
                                  onClick={() => {
                                    setOpenDropdowns(prev => {
                                      const newState = { ...prev };
                                      delete newState[`${question.id}-${shuffledLeftIndex}`];
                                      return newState;
                                    });
                                  }}
                                >
                                  Select a match...
                                </DropdownOption>
                                {shuffledRightItems.map((rightItem, shuffledRightIndex) => {
                                  if (!rightItem || !rightItem.trim()) return null;
                                  return (
                                    <DropdownOption
                                      key={shuffledRightIndex}
                                      type="button"
                                      onClick={() => {
                                        setOpenDropdowns(prev => {
                                          const newState = { ...prev };
                                          delete newState[`${question.id}-${shuffledLeftIndex}`];
                                          return newState;
                                        });
                                      }}
                                    >
                                      {rightItem || `Definition ${shuffledRightIndex + 1}`}
                                    </DropdownOption>
                                  );
                                })}
                              </DropdownMenu>,
                              document.body
                            )}
                          </CustomDropdown>
                        </MatchingItemGrid>
                      );
                    })}
                  </div>
                  
                  {/* Show correct answers below for instructor reference */}
                  {question.settings?.pairs && question.settings.pairs.length > 0 && (
                    <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
                      <strong style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block' }}>
                        Correct Answers (for instructor reference):
                      </strong>
                      {question.settings.pairs.map((pair, pairIndex) => {
                        const leftItem = question.settings.leftItems?.[pair.leftIndex] || '';
                        const rightItem = question.settings.rightItems?.[pair.rightIndex] || '';
                        return (
                          <CorrectOptionItem key={pairIndex} style={{ marginTop: pairIndex > 0 ? '0.5rem' : '0' }}>
                            <strong>{leftItem}</strong> → {rightItem}
                          </CorrectOptionItem>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </QuestionCard>
        ))}
      </QuestionsList>
    </Container>
  );
}

export default QuizPreview;
